import merge from 'lodash/merge'
import omit from 'lodash/omit'
import cloneDeep from 'lodash/cloneDeep'
import isArray from 'lodash/isArray'
import chroma from 'chroma-js'

import {
  numberToArea,
  numberToCurrency,
  numberToCurrencyPerArea,
  numberToDays,
  numberToHuman,
  numberToPercentage
} from '../../javascript/helpers/number_helper'
import { BaseChartController, colors, defaultBoldTextStyle, tooltipValueFormatter } from './base_chart_component_controller'

export class XYChartController extends BaseChartController {
  static values = merge(cloneDeep(BaseChartController.values), {
    axisLabel: String,
    xAxisShow: { type: Boolean, default: true },
    xAxisType: String,
    xAxisName: String,
    xAxisStartZero: { type: Boolean, default: false },
    yAxisShow: { type: Boolean, default: true },
    yAxisType: String,
    yAxisName: String,
    yAxisZero: { type: Boolean, default: false },
    yAxisStart: { type: Number, default: null },
    yAxisEnd: { type: Number, default: null },
    ySecondAxisShow: { type: Boolean, default: true },
    ySecondAxisType: String,
    ySecondAxisName: String,
    ySecondAxisZero: { type: Boolean, default: false },
    ySecondAxisStart: { type: Number, default: null },
    ySecondAxisEnd: { type: Number, default: null },
    tooltipAxisType: String,
    tooltipValueFormatter: { type: String, default: 'yAxis' },
    numberOfForecasts: { type: Number, default: 0 },
    markLine: Object
  })

  defaultSeriesStyle = {
    symbol: 'circle',
    symbolSize: 8,
    showSymbol: false,
    lineStyle: {
      width: 3
    }
  }

  transformToDataset (series) {
    return series.map((s) => {
      return {
        dimension: ['x', 'y'],
        source: s.data
      }
    })
  }

  transformToSeries ({ series, options, offset = 0 }) {
    return series.map((s, index) => {
      s.yAxisIndex = s.yAxisIndex || 0
      if (s.markBars === undefined) {
        s.markBars = []
      } else if (!isArray(s.markBars)) {
        s.markBars = [s.markBars]
      }

      const tooltip = {}
      if (this.tooltipValueFormatterValue === 'yAxis') {
        const type = (s.yAxisIndex === 1) ? this.ySecondAxisTypeValue : this.yAxisTypeValue
        tooltip.valueFormatter = tooltipValueFormatter(this.localeValue, type || this.yAxisTypeValue)
      }

      return merge(
        cloneDeep(this.defaultSeriesStyle),
        cloneDeep(options),
        omit(s, ['title', 'data', 'markBars']),
        {
          name: s.title,
          datasetIndex: index + offset,
          tooltip,
          markArea: this.markArea(s, this.numberOfForecastsValue),
          markLine: this.markLine(s),
          itemStyle: {
            color: function (params) {
              return s.markBars.includes(params.data.x) ? chroma(params.color).brighten().hex() : params.color
            }
          }
        }
      )
    })
  }

  seriesType () {
    return 'none'
  }

  options () {
    let yAxis = []
    if (this.ySecondAxisTypeValue) {
      yAxis = [
        this.yAxisOptions({
          minOptions: {
            start: this.yAxisStartValue,
            zero: this.yAxisZeroValue,
            seriesData: this.seriesValue[0].data
          },
          maxOptions: {
            end: this.yAxisEndValue,
            zero: this.yAxisZeroValue,
            seriesData: this.seriesValue[0].data
          }
        }),
        this.yAxisOptions({
          axisType: this.ySecondAxisTypeValue,
          show: this.ySecondAxisShowValue,
          name: this.ySecondAxisNameValue,
          minOptions: {
            start: this.ySecondAxisStartValue,
            zero: this.ySecondAxisZeroValue,
            seriesData: this.seriesValue[1].data
          },
          maxOptions: {
            end: this.ySecondAxisEndValue,
            zero: this.ySecondAxisZeroValue,
            seriesData: this.seriesValue[1].data
          }
        })
      ]
    } else {
      yAxis = [this.yAxisOptions({})]
    }

    return merge(super.options(), {
      xAxis: this.xAxisOptions(),
      yAxis,
      dataset: this.transformToDataset(this.seriesValue),
      series: this.transformToSeries({
        series: this.seriesValue,
        options: { type: this.seriesType() }
      }),
      grid: {
        left: 2,
        top: this.titleValue !== '' ? 40 : 10,
        bottom: this.gridBottomValue(),
        right: this.legendTypeValue === 'vertical' ? 160 : 30,
        containLabel: true
      }
    })
  }

  gridBottomValue () {
    switch (this.legendTypeValue) {
      case 'horizontal':
        return this.xAxisShowValue ? 35 : 20
      case 'horizontal_two_lines':
        return this.xAxisShowValue ? 60 : 45
      default:
        return 0
    }
  }

  tooltipOptions () {
    let type
    switch (this.tooltipValueFormatterValue) {
      case 'yAxis':
        type = this.yAxisTypeValue
        break
      case 'xAxis':
        type = this.xAxisTypeValue
        break
      default:
        type = this.tooltipValueFormatterValue
    }

    return merge(super.tooltipOptions(), {
      trigger: 'axis',
      axisPointer: tooltipAxisTypes(this.localeValue, this.tooltipAxisTypeValue),
      valueFormatter: tooltipValueFormatter(this.localeValue, type)
    })
  }

  tooltipFormatters () {
    return merge(super.tooltipFormatters(), {
      markLine: (params) => this.tooltipMarkLineFormatter(params)
    })
  }

  tooltipMarkLineFormatter (params) {
    const markLine = this.options()?.series[0]?.markLine
    const header = this.tooltipHeaderFormatter()(params)
    let markLineHtml = ''

    if (markLine?.data) {
      const formatter = this.tooltipOptions().valueFormatter
      const data = markLine.data[0]
      markLineHtml = `
        <div style="margin: 10px 0 0;line-height:1;">
          <span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:${markLine.lineStyle.color};"></span>
          <span style="font-size:14px;color:#666;font-weight:400;margin-left:2px">${data.name}</span>
          <span style="float:right;margin-left:20px;font-size:14px;color:#666;font-weight:900">${formatter(data.xAxis || data.yAxis)}</span>
          <div style="clear:both"></div>
        </div>
        <hr style="margin: 10px 0 0;line-height:1;"/>
      `
    }

    return `
      <div style="margin: 0px 0 0;line-height:1;">
        ${header}
        <div style="margin: ${header === '' ? '-10px' : '0px'} 0 0;line-height:1;">
          ${markLineHtml}
          ${params.map((series) => {
            const seriesConfig = this.options().series[series.seriesIndex]?.tooltipFormatter
            return this.tooltipSeriesFormatter(seriesConfig)(series)
          }).join('\n')}
        </div>
        <div style="clear:both"></div>
      </div>
    `
  }

  xAxisOptions () {
    return merge({
      show: this.xAxisShowValue,
      name: this.xAxisNameValue,
      nameLocation: 'middle',
      nameGap: 30,
      scale: true,
      min: this.xAxisStartZeroValue ? 0 : null,
      axisLabel: cloneDeep(defaultAxisLabel),
      axisLine: cloneDeep(defaultAxisLine),
      axisTick: cloneDeep(defaultXAxisTick)
    }, this.axisTypes()[this.xAxisTypeValue])
  }

  yAxisOptions ({ axisType = this.yAxisTypeValue, show = this.yAxisShowValue, name = this.yAxisNameValue, minOptions = {}, maxOptions = {} }) {
    return merge({
      show,
      name,
      scale: true,
      min: this.yAxisMinZero(minOptions),
      max: this.yAxisMaxZero(maxOptions),
      nameLocation: 'end',
      nameTextStyle: defaultBoldTextStyle,
      axisLabel: cloneDeep(defaultAxisLabel),
      axisLine: cloneDeep(defaultAxisLine)
    }, this.axisTypes()[axisType])
  }

  yAxisMinZero ({ start = this.yAxisStartValue, zero = this.yAxisZeroValue, seriesData = this.seriesValue.flatMap(s => s.data) }) {
    if (start) { return start }
    if (zero && !seriesData.some(p => p.y < 0)) { return 0 }
    return null
  }

  yAxisMaxZero ({ end = this.yAxisEndValue, zero = this.yAxisZeroValue, seriesData = this.seriesValue.flatMap(s => s.data) }) {
    if (end) { return end }
    if (zero && !seriesData.some(p => p.y > 0)) { return 0 }
    return null
  }

  axisTypes () {
    return {
      area: {
        type: 'value',
        minInterval: 1,
        axisLabel: { formatter: (value) => numberToArea(value, this.localeValue, { maximumFractionDigits: 0 }) }
      },
      value: {
        type: 'value',
        minInterval: 1,
        axisLabel: { formatter: (value) => numberToHuman(value, this.localeValue) }
      },
      days: {
        type: 'value',
        minInterval: 1,
        axisLabel: { formatter: (value) => numberToDays(value, this.localeValue, { maximumFractionDigits: 0 }) }
      },
      currency: {
        type: 'value',
        minInterval: 1,
        axisLabel: { formatter: (value) => numberToCurrency(value, this.localeValue, { maximumFractionDigits: 0 }) }
      },
      currency_by_area: {
        type: 'value',
        minInterval: 1,
        axisLabel: { formatter: (value) => numberToCurrencyPerArea(value, this.localeValue, { maximumFractionDigits: 1 }) }
      },
      percentage: {
        type: 'value',
        axisLabel: { formatter: (value) => numberToPercentage(value, this.localeValue, { maximumFractionDigits: 1 }) }
      },
      datetime: {
        type: 'time',
        minInterval: 86400000
      },
      date: {
        type: 'time',
        axisLabel: { formatter: '{dd}.{MM}.{yyyy}' },
        minInterval: 86400000
      },
      monthly: {
        type: 'time',
        axisLabel: { formatter: '{MMM} {yyyy}' },
        minInterval: 86400000
      },
      quarterly: {
        type: 'time',
        axisLabel: { formatter: 'Q{Q}\' {yyyy}' },
        minInterval: 86400000
      },
      yearly: {
        type: 'time',
        axisLabel: { formatter: '{yyyy}' },
        minInterval: 86400000
      },
      category: {
        type: 'category'
      },
      none: {
        type: 'value',
        axisLabel: { formatter: (_value) => '' }
      }
    }
  }

  markArea (s, numberOfForecasts) {
    if (!numberOfForecasts || !s.data.length) return

    return {
      silent: true,
      data: [[
        { xAxis: s.data.at(-1 * numberOfForecasts).x },
        { xAxis: s.data.at(-1).x }
      ]],
      itemStyle: {
        color: colors.base[300]
      }
    }
  }

  markLine (s) {
    if (!this.markLineValue || !s.data.length) return

    return merge(
      {
        silent: true,
        label: {
          position: 'insideEndBottom',
          formatter: ({ data }) => {
            const formatter = this.tooltipOptions().valueFormatter
            return `${data.name}: ${formatter(data.value)}`
          }
        },
        symbol: ['none', 'none'],
        lineStyle: {
          type: 'solid',
          color: colors.error,
          width: 2,
          cap: 'round'
        }
      }, this.markLineValue)
  }
}

function tooltipAxisTypes (locale, type) {
  return {
    percentage: {
      label: {
        formatter: ({ value }) => {
          return numberToPercentage(value, locale, { maximumFractionDigits: 1 })
        }
      }
    },
    date: {
      label: {
        formatter: ({ value }) => {
          return new Date(value).toLocaleDateString(locale)
        }
      }
    },
    monthly: {
      label: {
        formatter: ({ value }) => {
          const date = new Date(value)
          return `${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()}`
        }
      }
    },
    quarterly: {
      label: {
        formatter: ({ value }) => {
          const date = new Date(value)
          const quarter = Math.floor(date.getMonth() / 3) + 1
          return `Q${quarter}' ${date.getFullYear()}`
        }
      }
    },
    yearly: {
      label: {
        formatter: ({ value }) => {
          const date = new Date(value)
          return `${date.getFullYear()}`
        }
      }
    }
  }[type]
}

const defaultAxisLabel = {
  fontSize: 14,
  margin: 12,
  showMinLabel: true,
  showMaxLabel: true,
  hideOverlap: true
}

const defaultLineStyle = {
  color: colors.neutral[300]
}

const defaultAxisLine = {
  show: true,
  lineStyle: defaultLineStyle
}

const defaultXAxisTick = {
  inside: true,
  length: 5,
  lineStyle: {
    shadowColor: colors.neutral[300],
    shadowOffsetY: 5,
    shadowOffsetX: 0,
    shadowBlur: 0
  }
}

export default {
  XYChartController
}
